import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { LoaderCircleIcon } from "lucide-react";
import { cn } from "../../utils";
import { RedDot } from "./Dot";

const variants = {
  variants: {
    variant: {
      default: "bg-primary hover:bg-primary/90 text-blue-500 shadow",
      null: "",
      destructive:
        "bg-destructive-background text-destructive-foreground hover:bg-destructive-background/90",
      "outline-destructive":
        "border-destructive bg-destructive-background text-destructive-foreground hover:bg-destructive-background/90 border",
      "destructive-filled":
        "bg-destructive text-destructive-background hover:bg-destructive/90 shadow",
      outline:
        "border-input bg-background hover:bg-accent hover:text-accent-foreground border shadow-sm",
      secondary: "bg-secondary-500 hover:bg-secondary-400 text-white shadow-sm",
      ghost: "hover:bg-accent hover:text-accent-foreground",
      "ghost-blue": "hover:bg-accent font-bold text-blue-500 hover:text-blue-600",
      transparent: "hover:opacity-80",
      link: "text-primary underline-offset-4 hover:underline",
      blue: "bg-primary-blue hover:bg-primary-blue/90 text-white shadow-none",
      red: "bg-primary-red hover:bg-primary-red/90 text-white shadow-none",
      "outline-blue":
        "bg-blue-25 hover:bg-primary-100 border border-blue-400 text-blue-600 shadow-none",
      "outline-red":
        "bg-red-25 hover:bg-primary-100 border border-red-400 text-red-600 shadow-none",
      "outline-white":
        "border border-gray-300 bg-white text-blue-500 shadow-none hover:bg-blue-100",
      "third-blue": "bg-primary-50 text-primary-700 hover:bg-primary-100 shadow-none",
      "outline-white-blue":
        "border-primary-400 text-primary-600 border bg-white shadow-none hover:bg-blue-100",
      disabled: "bg-gray-400 text-gray-700",
      hidden: "block rounded-none",
      unstyled: "",
    },
    size: {
      default: "h-9 px-4 py-2",
      sm: "h-8 rounded-md px-3 text-xs",
      lg: "h-10 rounded-md px-8",
      icon: "size-9",
      fit: "size-fit",
      full: "size-full",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
} as const;

const buttonVariants = cva(
  "focus-visible:ring-ring inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50",
  variants,
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  hasPing?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className: classNameProps,
      variant,
      size,
      disabled,
      isLoading,
      children,
      asChild = false,
      hasPing,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const className =
      variant === "unstyled"
        ? cn(size ? variants.variants.size[size] : "", classNameProps)
        : cn(buttonVariants({ variant, size, className: classNameProps }));

    return (
      <Comp className={className} disabled={disabled || isLoading} ref={ref} {...props}>
        <>
          {children}
          {isLoading && <LoaderCircleIcon className="ml-2 animate-spin" />}
          {hasPing && <RedDot className="ml-2 size-1" />}
        </>
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
